'use client';
import { useTranslations } from 'next-intl';
import { type CSSProperties, forwardRef, type ReactNode } from 'react';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { BaseLinePlus } from '@/app/components/icons/BaseLinePlus';
import { BaseLineReplace } from '@/app/components/icons/BaseLineReplace';
import { Loading } from '@/app/components/icons/Loading';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { ImageOperationDialog } from '@/app/components/shared/ImageOperationDialog';
import { ImageUploader, type ImageUploaderProps } from '@/app/components/shared/ImageUploader';
import { ImageWithPlaceholder } from '@/app/components/shared/ImageWithPlaceholder';
import { MAX_IMAGE_FILE_SIZE } from '@/app/constants';
import { useDragAndDrop } from '@/app/hooks';
import type { ImagePrompt } from '@/app/types';
import { cn } from '@/app/utils';

export type showModeType = 'source' | 'cropped';
type OperationItem = {
  key: string;
  icon: ReactNode;
  text: string;
  onClick: () => void;
  disabled?: boolean;
};

interface ImageUploaderWithPreviewProps extends ImageUploaderProps {
  loading?: boolean;
  placeholder?: ReactNode | string | null;
  // 控制是否展示辅助性文案（placeholder 与 "上传中..."文案）
  showHelperText?: boolean;
  image: ImagePrompt | null; // 展示的已上传文件
  showMode?: showModeType; // 展示模式，source 为原图，cropped 为裁剪后的图片
  operations?: OperationItem[]; // 操作按钮列表
  operationIconClassName?: string;
  closeBoxClassName?: string;
  closeIconClassName?: string;
  innerClassName?: string;
  imageContainerClassName?: string;
  imageClassName?: string;
  uploaderClassName?: string;
  imgStyle?: CSSProperties; // 图片样式设置
  uploadIcon?: ReactNode; // 上传图标
  idx?: number; // 用于拖拽排序
  draggable?: boolean;
  onItemsReorder?: (sourceIdx: number, targetIdx: number) => void;
  deleteTooltip?: string; // 删除按钮的提示文案
}

/**
 * 包含图片上传、图片操作和图片预览功能的业务组件，用于大部分涉及图片上传和展示的表单（例如图生视频、模版）
 *
 * 支持配置 draggable 来开启拖拽排序
 * 默认操作包括图片替换和删除，可以通过 operations 配置其他操作
 *
 * @param {ImageUploaderWithPreviewProps} props
 * @returns {ReactNode} 图片上传组件
 */
export const ImageUploaderWithPreview = forwardRef<HTMLDivElement, ImageUploaderWithPreviewProps>(
  (
    {
      className,
      maxCount = 1,
      disabled,
      loading,
      placeholder,
      showHelperText = true,
      maxFileSize = MAX_IMAGE_FILE_SIZE,
      isInitialSelectedImageRange = true,
      scene,
      getSelectedImage,
      onChange,
      image,
      showMode = 'cropped',
      operations,
      operationIconClassName,
      closeBoxClassName,
      closeIconClassName,
      innerClassName,
      imageContainerClassName,
      imageClassName,
      imgStyle,
      uploaderClassName,
      uploadIcon = <BaseLinePlus className="flex-shrink-0 text-2xl text-system-white" />,
      idx = 0,
      draggable,
      onItemsReorder,
      deleteTooltip,
      allowTempMode = false,
      dialogTheme,
    },
    ref,
  ) => {
    const t = useTranslations();

    const { onDragOver, onDragStart, onDropCapture } = useDragAndDrop({
      onReorder: onItemsReorder,
    });

    const handleRemoveImage = () => {
      if (image) {
        URL.revokeObjectURL(image.sourceImagePreview);
        if (image.selectedImagePreview) {
          URL.revokeObjectURL(image.selectedImagePreview);
        }
      }
      onChange?.({
        id: idx.toString(),
        file: null,
        status: 'removed',
      });
    };

    return (
      <div
        draggable={draggable}
        onDragStart={(e) => draggable && onDragStart(e, idx)}
        onDragOver={draggable ? onDragOver : undefined}
        onDropCapture={(e) => draggable && onDropCapture(e, idx)}
        className={cn(
          'group flex h-full w-full max-w-full flex-col items-center justify-center rounded-10 bg-system-black36 text-system-white64',
          draggable && 'cursor-grab',
          className,
        )}
        ref={ref}
      >
        {loading ? (
          <>
            <Loading className="animate-spin text-2xl" />
            {showHelperText && <div className="mt-2 sm:text-xs">{t('create.createForm.upload.uploading')}</div>}
          </>
        ) : image ? (
          <div className={cn('flex h-full w-full items-center', imageContainerClassName)}>
            <div className="group relative h-full w-full min-w-[40%] flex-1 rounded-10 bg-system-black36">
              <ImageWithPlaceholder
                src={showMode === 'cropped' ? image.selectedImagePreview : image.sourceImagePreview}
                className={cn('h-full w-full rounded-10 bg-system-black36 object-contain', imageClassName)}
                alt="upload preview"
                style={imgStyle}
              />
              <div
                className={cn(
                  'absolute inset-0 flex items-center justify-center gap-4 rounded-10 bg-system-black36 px-4 opacity-0 transition-opacity group-hover:opacity-100 sm:hidden',
                  innerClassName,
                )}
              >
                <ImageUploader
                  className={cn('cursor-pointer text-[28px]', operationIconClassName)}
                  key="upload-new"
                  maxCount={1}
                  maxFileSize={maxFileSize}
                  isInitialSelectedImageRange={isInitialSelectedImageRange}
                  scene={scene}
                  getSelectedImage={getSelectedImage}
                  onChange={onChange}
                  allowTempMode={allowTempMode}
                >
                  <BaseLineReplace />
                </ImageUploader>

                {operations?.map((operation) => (
                  <div
                    className={cn(
                      'cursor-pointer text-[28px]',
                      operation.disabled && 'cursor-not-allowed text-system-white24',
                      operationIconClassName,
                    )}
                    key={operation.key}
                    onClick={!operation.disabled ? operation.onClick : undefined}
                  >
                    {operation.icon}
                  </div>
                ))}
                <CustomTooltip content={deleteTooltip} side="top" sideOffset={8} disableHoverableContent={true}>
                  <div
                    className={cn(
                      '-top-2 -right-2 absolute flex size-6 cursor-pointer items-center justify-center rounded-full bg-system-white24 text-system-white backdrop-blur-[2px]',
                      closeBoxClassName,
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleRemoveImage();
                    }}
                  >
                    <BaseLineClose className={cn('text-xl', closeIconClassName)} />
                  </div>
                </CustomTooltip>
              </div>

              <ImageOperationDialog
                image={image}
                showMode={showMode}
                operations={operations}
                handleRemoveImage={handleRemoveImage}
              />
            </div>
          </div>
        ) : (
          <ImageUploader
            className={cn(
              'inline-flex h-full w-full flex-col items-center justify-center rounded-10',
              disabled ? 'cursor-not-allowed text-system-white24' : 'cursor-pointer hover:bg-system-black64',
              innerClassName,
              uploaderClassName,
            )}
            maxCount={maxCount}
            disabled={disabled}
            maxFileSize={maxFileSize}
            isInitialSelectedImageRange={isInitialSelectedImageRange}
            scene={scene}
            getSelectedImage={getSelectedImage}
            onChange={onChange}
            allowTempMode={allowTempMode}
            dialogTheme={dialogTheme}
          >
            {uploadIcon}
            {showHelperText && (
              <div className="mx-2 mt-2 text-center text-xs sm:mt-1.5">
                {placeholder || t('create.createForm.upload.placeholder')}
              </div>
            )}
          </ImageUploader>
        )}
      </div>
    );
  },
);
